import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { InboundRoute } from '@arcnet/contracts';
import { useRouteHistory, useRouteVersion } from '../../lib/api/hooks-tms.js';
import { Modal } from '../../components/ui/Modal.js';
import { Icon } from '../../components/ui/Icon.js';
import { ErrorBanner } from '../../components/ui/Primitives.js';
import { FlowChartSvg, layoutFlow } from './flowchart.js';
import { buildRouteTree, type Lookup } from './routeTree.js';
import { liveAsOf, type Live } from './flows/model.js';
import { useLiveRouting } from './flows/useLive.js';
import { namesFrom } from './flows/describe.js';
import { HistoryList, NameVersion, StepChanges, historySince } from './flows/HistoryList.js';
import { RestoreDialog } from './flows/RestoreDialog.js';
import { clock, rowsOf, shortAt, sourceText } from './flows/history.js';

/**
 * A route's history in Classic (docs/41 §12, Fable's design): from the route's own head, a window
 * with the versions on the left, and on the right what the chosen one changed above the route's
 * diagram as it was right after it. Restoring applies at once, as every Classic save does.
 */
export function RouteHistory({ tenantId, routeId, routeName, canName, canPublish }: {
  tenantId: string; routeId: string; routeName: string; canName: boolean; canPublish: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={() => setOpen(true)}>
        <Icon name="history" size={13} />History
      </button>
      {open && <HistoryWindow tenantId={tenantId} routeId={routeId} routeName={routeName}
        canName={canName} canPublish={canPublish} onClose={() => setOpen(false)} />}
    </>
  );
}

const ZOOM_MIN = 0.2, ZOOM_MAX = 2;

function HistoryWindow({ tenantId, routeId, routeName, canName, canPublish, onClose }: {
  tenantId: string; routeId: string; routeName: string; canName: boolean; canPublish: boolean; onClose: () => void;
}) {
  const { live } = useLiveRouting(tenantId);
  const historyQ = useRouteHistory(tenantId, routeId);
  const [at, setAt] = useState<string | null>(null);
  const versionQ = useRouteVersion(tenantId, routeId, at);
  const [restoring, setRestoring] = useState(false);
  const versions = historyQ.data?.data ?? [];
  const rows = useMemo(() => rowsOf(versions), [versions]);
  const row = rows.find((r) => r.id === (at ?? rows[0]?.id)) ?? null;
  const viewing = at && versionQ.data?.version.id === at ? versionQ.data : null;

  const names = useMemo(() => namesFrom(live, [
    ...versions.flatMap((v) => v.steps.map((s) => ({ kind: s.kind, id: s.id, body: s.after ?? s.before }))),
    ...(viewing?.pieces ?? []),
  ]), [live, versions, viewing]);

  // The diagram as it was right after the chosen version, with what it did marked.
  const shown: Live = useMemo(() => (viewing ? liveAsOf(live, viewing.pieces) : live), [viewing, live]);
  const root = useMemo(() => {
    const r = shown.routes.find((x) => x.id === routeId) as InboundRoute | undefined;
    if (!r || (at && !viewing)) return null;
    const marks = new Map((row?.steps ?? []).flatMap((s) => (s.after ? [[s.id, s.before ? 'changed' : 'new'] as const] : [])));
    const data: Lookup = {
      ext: new Map(shown.extensions.map((e) => [e.id, e])),
      groups: new Map(shown.groups.map((g) => [g.id, g])),
      queues: new Map(shown.queues.map((q) => [q.id, q])),
      ivrs: new Map(shown.ivrs.map((m) => [m.id, m])),
      messages: new Map(shown.messages.map((m) => [m.id, m])),
      hours: new Map(shown.hours.map((h) => [h.id, h])),
      boxes: new Map(shown.boxes.map((b) => [b.id, b])),
      marks,
    };
    const split = r.hours.mode !== 'always';
    return buildRouteTree({
      numbers: r.numbers.map((n) => n.e164), destination: r.destination,
      hours: split ? { label: r.hours.mode === 'own' ? 'Custom hours' : `Schedule: ${r.hours.schedule_name ?? 'not chosen'}`, openNow: null } : null,
      closed: split ? r.closed_destination : null, enabled: r.is_enabled, mark: marks.get(routeId),
    }, data);
  }, [shown, routeId, row, at, viewing]);
  const chart = useMemo(() => (root ? layoutFlow(root) : null), [root]);

  // Opens at Fit, like the flow diagram (owner, 7 October 2026).
  const pane = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState<number | null>(null);
  const [fit, setFit] = useState(1);
  useLayoutEffect(() => {
    const el = pane.current;
    if (!el || !chart) return undefined;
    const measure = () => setFit(Math.max(ZOOM_MIN, Math.min(1.25, (el.clientWidth - 32) / chart.width, (el.clientHeight - 32) / chart.height)));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [chart]);
  const scale = zoom ?? fit;
  const clamp = (z: number) => Math.round(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z)) * 100) / 100;
  const drag = useRef<{ x: number; y: number; left: number; top: number } | null>(null);

  const v = row?.versions[0] ?? null;
  return (
    <Modal title={`History · ${routeName}`} size="full" onClose={onClose} actions={(
      <>
        <div className="arc-row arc-history-zoom" style={{ gap: 2 }} role="group" aria-label="Zoom">
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" aria-label="Zoom out" onClick={() => setZoom(clamp(scale / 1.25))}>−</button>
          <span className="arc-subtle arc-num" style={{ minWidth: 44, textAlign: 'center', fontSize: 12 }} aria-live="polite">{Math.round(scale * 100)}%</span>
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" aria-label="Zoom in" onClick={() => setZoom(clamp(scale * 1.25))}>+</button>
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" aria-pressed={zoom === null} onClick={() => setZoom(null)}>Fit</button>
        </div>
        {canPublish && at && viewing && (
          <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" onClick={() => setRestoring(true)}>
            <Icon name="restore" size={13} />Restore this version
          </button>
        )}
      </>
    )}>
      <div className="arc-history-modal">
        <div className="arc-history-side">
          {versions.length > 0 && (
            <div className="arc-subtle" style={{ padding: '10px 14px 0', fontSize: 12 }}>
              {historySince(versions).replace(/^since/, 'Since')}
            </div>
          )}
          {historyQ.error ? <div style={{ padding: 14 }}><ErrorBanner error={historyQ.error} /></div>
            : historyQ.isLoading ? <div style={{ padding: 14 }}><div className="arc-skeleton" style={{ height: 160 }} /></div>
              : <HistoryList tenantId={tenantId} versions={versions} names={names} viewing={at} onView={setAt}
                  canName={canName} inline={false} />}
        </div>
        <div className="arc-history-main">
          {row && v && (
            <div className="arc-history-changes">
              <div className="arc-history-changes-head">
                <div className="arc-stack">
                  <strong>{row.versions.length > 1 ? `${shortAt(row.versions[row.versions.length - 1]!.at)}–${clock(v.at)}` : shortAt(v.at)}</strong>
                  <span className="arc-muted">
                    {[v.by, sourceText(v), row.versions.length > 1 ? `${row.versions.length} saves` : null].filter(Boolean).join(' · ')}
                  </span>
                </div>
                {canName && <NameVersion key={`${v.id}:${v.name ?? ''}`} tenantId={tenantId} version={v} />}
              </div>
              {v.source === 'start'
                ? <p className="arc-muted" style={{ margin: 0, fontSize: 12.5 }}>History starts here: the routing as it was when version history began.</p>
                : <div className="arc-history-changes-grid"><StepChanges steps={row.steps} names={names} /></div>}
            </div>
          )}
          <div ref={pane} className="arc-fc-viewport"
            onPointerDown={(e) => {
              if (e.button !== 0) return;
              drag.current = { x: e.clientX, y: e.clientY, left: e.currentTarget.scrollLeft, top: e.currentTarget.scrollTop };
              e.currentTarget.setPointerCapture(e.pointerId);
            }}
            onPointerMove={(e) => {
              const d = drag.current;
              if (!d) return;
              e.currentTarget.scrollLeft = d.left - (e.clientX - d.x);
              e.currentTarget.scrollTop = d.top - (e.clientY - d.y);
            }}
            onPointerUp={() => { drag.current = null; }} onPointerCancel={() => { drag.current = null; }}>
            {chart ? <FlowChartSvg chart={chart} zoom={scale} label={`${routeName} as it was`} />
              : <div className="arc-skeleton" style={{ height: 240, margin: 16 }} />}
          </div>
        </div>
      </div>
      {restoring && viewing && at && (
        <RestoreDialog tenantId={tenantId} routeId={routeId} routeName={routeName} versionId={at} at={viewing.version.at}
          plan={viewing.plan} names={names} onClose={() => setRestoring(false)}
          onRestored={() => { setRestoring(false); setAt(null); }} />
      )}
    </Modal>
  );
}
